import { defineNuxtPlugin } from '#app';
import axios, { type AxiosRequestConfig } from 'axios';
import { useToast } from 'primevue/usetoast';

export interface CustomAxiosRequestConfig<T = any> extends AxiosRequestConfig<T> {
    suppressToast?: boolean;
}

export default defineNuxtPlugin(() => {
    const { $keycloak } = useNuxtApp();

    const toast = useToast();

    axios.defaults.baseURL = import.meta.env.VITE_API_COMIC_BOOK_URL;
    axios.defaults.headers.common['Content-Type'] = 'application/json; charset=UTF-8';

    axios.interceptors.request.use(
        (config) => {
            if ($keycloak?.token) {
                config.headers.Authorization = `Bearer ${$keycloak.token}`;
            }
            return config;
        },
        (error) => Promise.reject(error),
    );

    axios.interceptors.response.use(
        (response) => response,
        async (error) => {
            const { response } = error;

            const suppressToast = response?.config?.suppressToast;

            if (!suppressToast && response?.data?.details) {
                const firstPropertyKey = Object.keys(response.data.details)[0];
                const firstErrorMessage = response.data.details[firstPropertyKey];

                const fullErrorMessage = firstPropertyKey
                    ? `${firstPropertyKey}: ${firstErrorMessage}`
                    : response.data.message;

                toast.add({
                    severity: 'error',
                    detail: fullErrorMessage,
                    life: 5000,
                    group: 'default',
                });
            } else if (!suppressToast) {
                toast.add({
                    severity: 'error',
                    detail: response.data.error,
                    life: 5000,
                    group: 'default',
                });
            }

            if (response?.status === 401) {
                if (!$keycloak?.authenticated) {
                    try {
                        const nuxtApp = useNuxtApp();
                        const locale = nuxtApp.$i18n?.locale?.value || 'en';

                        await $keycloak.login({ locale });
                    } catch (e) {
                        // If Nuxt app context is not available, continue without locale
                        await $keycloak.login();
                    }
                } else {
                    try {
                        await $keycloak.updateToken(10);

                        const config = error.config;
                        config.headers.Authorization = `Bearer ${$keycloak.token}`;
                        //  return axios(config);
                    } catch (refreshError) {
                        await $keycloak.login();
                    }
                }
            }

            return Promise.reject(error);
        },
    );
});
